import { GridApi, gridDateComparator, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { mergeRight } from 'ramda';
import { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';
import { v4 as uuidV4 } from 'uuid';

import { useGridValidator, useValidationTrigger } from '../../components/IntakeDataGrid/IntakeDataGrid.hooks';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  ChipCell,
  ContentBlockCell,
  CurrencyCell,
  DataGridConfig,
  DateRangeCell,
  DateRangeCellValue,
  DeleteActionCell,
  editCellFactory,
  EmptyFallbackCell,
  gridColDefFactory,
  NULLISH_CELL_VALUE,
  UseDataEditGridConfig,
} from '~/components/DataGridCustom';

import { useTotalLineItemFormat } from './components/IntakeRequestStepLineItemTotal/IntakeRequestStepLineItemTotal.hooks';
import {
  IntakeRequestLineItem,
  intakeRequestLineItemArraySchema,
  lineItemDefault,
} from './IntakeRequestLineItemStep.schema';
import {
  IntakeRequestStepLineItemGridField,
  IntakeRequestStepLineItemGridRow,
} from './IntakeRequestLineItemStep.types';

const pathPrefix = ['lineItems'];

export const useIntakePurchaseRequisitionGridConfig: UseDataEditGridConfig<IntakeRequestStepLineItemGridRow> = ({
  getRowIndex,
  onRowDelete,
  disabled,
}) => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'IntakePurchaseRequisitionLineItemGrid.column',
  });
  const { getTotal } = useTotalLineItemFormat();

  return useMemo<DataGridConfig<IntakeRequestStepLineItemGridRow>>(() => {
    const standardCellConfig = {
      pathPrefix,
      getRowIndex,
      languageNamespace: I18nNamespace.intake,
      keyPrefix: 'IntakePurchaseRequisitionLineItemGrid.column',
      disabled,
    };

    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });

    return {
      sortModel: [],
      columns: [
        createColumn({
          field: IntakeRequestStepLineItemGridField.POSITION,
          minWidth: 50,
          hideable: false,
          resizable: false,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: IntakeRequestStepLineItemGridField.TITLE,
          minWidth: 450,
          display: 'text',
          valueGetter: (_value, row) => {
            return { title: row.title, description: row.description };
          },
          renderCell: cellFactory(ContentBlockCell),
        }),
        createColumn({
          field: IntakeRequestStepLineItemGridField.COMMODITY_GROUP_ID,
          minWidth: 150,
          valueGetter: (_value, row) => {
            const value = row.commodityGroupId;

            if (value) {
              return <CuriosityValue ns={CuriosityNamespaceType.COMMODITY_GROUP} i18nKey={value} />;
            }

            return value;
          },
          renderCell: cellFactory(ChipCell, ({ value }) => ({
            value,
            size: 'small' as const,
          })),
        }),
        createColumn({
          field: IntakeRequestStepLineItemGridField.QUANTITY,
          minWidth: 120,
          renderCell: ({ row }) => (
            <Trans
              t={tColumn}
              i18nKey="quantityUnit"
              // render 0 as - because it's a required field indicating incorrect data
              values={{ quantity: row.quantity || NULLISH_CELL_VALUE }}
              components={{
                CuriosityValue: <CuriosityValue ns={CuriosityNamespaceType.UNIT} i18nKey={row.unitId} />,
              }}
            />
          ),
        }),
        createColumn({
          field: IntakeRequestStepLineItemGridField.DELIVERY_TIME,
          minWidth: 250,
          renderCell: cellFactory(DateRangeCell),
          valueGetter: (_value, row): DateRangeCellValue => ({
            from: row.earlyDeliveryAt,
            to: row.lateDeliveryAt,
          }),
          sortComparator: (v1, v2, cellParams1, cellParams2) => {
            return gridDateComparator(v1?.to, v2?.to, cellParams1, cellParams2);
          },
        }),
        createColumn({
          field: IntakeRequestStepLineItemGridField.EXPCTED_PRICE_PER_UNIT,
          minWidth: 120,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ value: row.expectedPricePerUnit })),
        }),
        createColumn({
          field: IntakeRequestStepLineItemGridField.EXPCTED_TOTAL_PRICE_PER_UNIT,
          minWidth: 120,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ value: getTotal(row) })),
        }),
        ...(!disabled
          ? [
              createColumn({
                field: IntakeRequestStepLineItemGridField.DELETE_ACTION,
                minWidth: 72,
                width: 72,
                maxWidth: 72,
                renderHeader: () => null,
                align: 'left',
                renderCell: editCellFactory(DeleteActionCell, {
                  ...standardCellConfig,
                  pathPrefix,
                  onClick: onRowDelete,
                }),
              }),
            ]
          : []),
      ],
    };
  }, [getRowIndex, disabled, tColumn, t, onRowDelete, getTotal]);
};

export type LineItemDefaultValues = { commodityGroupId: Union.Nullable<string> };

export const useLineItemsGridHandlers = (
  apiRef: MutableRefObject<GridApi>,
  { commodityGroupId }: LineItemDefaultValues,
) => {
  const { triggeredValidations, shouldValidate } = useValidationTrigger();

  const { getRawRow, getRawRows } = useGridValidator({
    apiRef,
    schema: intakeRequestLineItemArraySchema,
  });

  const handleAddRow = useCallback(() => {
    const id = uuidV4();
    const items = getRawRows();
    const sequence = items.length + 1;
    const newLineItem = {
      ...mergeRight(lineItemDefault, { commodityGroupId }),
      id,
      position: `${sequence}`,
      sequence,
    } as IntakeRequestLineItem;

    apiRef.current.updateRows([{ ...newLineItem, action: 'insert' }]);
    apiRef.current.selectRow(id, true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commodityGroupId]);

  const handleRowDelete = useCallback((id: any) => {
    apiRef.current.updateRows([{ id, _action: 'delete' }]);
    apiRef.current.selectRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseDrawer = useCallback(() => {
    if (apiRef.current && apiRef.current.getSelectedRows) {
      const ids = Array.from(apiRef.current.getSelectedRows().keys());
      apiRef.current.selectRows(ids, false);
      shouldValidate(ids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const currentSelectedRowId = useMemo(() => rowSelectionModel[0], [rowSelectionModel]);

  const handleLineItemChange = useCallback(
    (lineItem: IntakeRequestLineItem) => {
      const id = rowSelectionModel[0];

      // Enforce validation only, if previous action wasn't the initial insert (handleAddRow)
      // Validation is enforced on closing the drawer latest (handleCloseDrawer)
      // if any update takes place (action is update), we want to validate the row
      const lastGridRowAction = apiRef?.current.getRowModels().get(id)?.action;
      if (lastGridRowAction !== 'insert') {
        shouldValidate(id);
      }
      apiRef?.current.updateRows([{ ...lineItem, id, action: 'update' }]);
    },
    [apiRef, rowSelectionModel, shouldValidate],
  );

  return {
    currentSelectedRowId,
    validate: triggeredValidations[currentSelectedRowId],
    selectedLineItem: getRawRow(currentSelectedRowId),
    setRowSelectionModel,
    handleAddRow,
    handleRowDelete,
    handleCloseDrawer,
    handleLineItemChange,
  };
};
